import { Sdk, User } from "../../core/internal/model/model"
import { InternalConfig } from "./Configurations.browser"
import { Transport } from "../../core/internal/transport/Transport"
import { IStorage, KeyTransformStorage } from "../../core/internal/storage/Storage"
import { Synchronizer } from "../../core/internal/sync/Synchronizer"
import { EvaluationMode } from "../../public/model"
import { Clock } from "../../core/internal/util/TimeUtil"
import { Urls } from "../http/Urls"
import { WorkspaceManager } from "../../core/internal/workspace/WorkspaceManager"

import WorkspaceConfigFetcher from "../../core/internal/workspace/config/WorkspaceConfigFetcher"
import { NoopWorkspaceConfigFetcher } from "../../core/internal/workspace/config/NoopWorkspaceConfigFetcher"
import { BrowserHttpWorkspaceFetcher } from "../../core/internal/workspace/config/HttpWorkspaceFetcher"
import { WorkspaceConfigManager } from "../../core/internal/workspace/config/WorkspaceConfigManager"
import { BrowserWorkspaceConfigStorage } from "../workspace/BrowserWorkspaceConfigStorage"

import WorkspaceEvaluationFetcher from "../../core/internal/workspace/evaluation/WorkspaceEvaluationFetcher"
import { NoopWorkspaceEvaluationFetcher } from "../../core/internal/workspace/evaluation/NoopWorkspaceEvaluationFetcher"
import WorkspaceEvaluationManager from "../../core/internal/workspace/evaluation/WorkspaceEvaluationManager"
import { BrowserWorkspaceEvaluationStorage } from "../../core/internal/workspace/evaluation/WorkspaceEvaluationStorage"
import { LruWorkspaceEvaluationCache } from "../../core/internal/workspace/evaluation/WorkspaceEvaluationCache"
import { HttpRemoteEvaluateClient } from "../../core/internal/workspace/evaluation/client/HttpRemoteEvaluateClient"
import FullWorkspaceRemoteEvaluator from "../../core/internal/workspace/evaluation/evaluator/full/FullWorkspaceRemoteEvaluator"
import PartialWorkspaceRemoteEvaluator from "../../core/internal/workspace/evaluation/evaluator/partial/PartialWorkspaceRemoteEvaluator"

import { UserManager } from "../../core/internal/user/UserManager"
import { UserStorage } from "../user/manager/UserStorage"
import { LocalUserManager } from "../user/manager/LocalUserManager"
import { RemoteUserManager } from "../user/manager/RemoteUserManager"
import { UserCohortFetcher } from "../../core/internal/user/UserCohortFetcher"
import { UserTargetFetcher } from "../../core/internal/user/UserTargetFetcher"
import { CampaignParser } from "../attribution/CampaignParser"
import { PageManager } from "../../core/internal/page/PageManager"

/**
 * The evaluation-mode-specific parts of the browser client: everything whose wiring differs between
 * local and remote evaluation. `createBrowserClient` builds one bundle (by mode) and consumes it,
 * keeping the rest of the bootstrap mode-agnostic.
 */
export interface EvaluationModeComponents {
  /** Consumed by ~12 subsystems (session/pii/campaign/iam/engagement/page/split/explorer/...). */
  userManager: UserManager
  /** IAM/GA/Split config reads + HackleCore config introspection. Remote degrades to a no-op fetcher. */
  workspaceConfigFetcher: WorkspaceConfigFetcher
  /** HackleCore's remote decision source. Local degrades to a no-op fetcher. */
  workspaceEvaluationFetcher: WorkspaceEvaluationFetcher
  /** Entity re-evaluation transport for deliver-time consumers; null in local mode. */
  workspaceEvaluationManager: WorkspaceEvaluationManager | null
  /** Active local/remote workspace source used by event metadata and shared workspace consumers. */
  workspaceManager: WorkspaceManager
  /** Synchronizers the caller registers on the composite (order matters). */
  synchronizers: Synchronizer[]
}

/** The shared bootstrap locals both builders draw from (relocated, not newly created). */
export interface EvaluationModeDeps {
  sdk: Sdk
  sdkKey: string
  config: InternalConfig
  defaultTransport: Transport
  localStorage: IStorage
  hackleDeviceId: string
  userStorage: UserStorage
  userCohortFetcher: UserCohortFetcher
  userTargetFetcher: UserTargetFetcher
  campaignParser: CampaignParser
  pageManager: PageManager
  clock: Clock
  previousUser: User | null
  initUser: User | null
}

/** Builds the evaluation-mode components for the given mode. Unknown modes degrade to local. */
export function buildEvaluationComponents(mode: EvaluationMode, deps: EvaluationModeDeps): EvaluationModeComponents {
  switch (mode) {
    case "remote":
      return buildRemoteEvaluationComponents(deps)
    case "local":
    default:
      return buildLocalEvaluationComponents(deps)
  }
}

export function buildLocalEvaluationComponents(deps: EvaluationModeDeps): EvaluationModeComponents {
  const scopedStorage = KeyTransformStorage.postfix(deps.localStorage, `_${deps.sdkKey.slice(0, 8)}`)
  // remote-mode evaluation cache is never read in local mode; drop it so a stale blob can't linger
  BrowserWorkspaceEvaluationStorage.clear(scopedStorage)
  const configStorage = new BrowserWorkspaceConfigStorage(deps.sdk, scopedStorage)
  const httpWorkspaceFetcher = new BrowserHttpWorkspaceFetcher(
    Urls.workspaceConfig(deps.config.sdkUrl, deps.sdk.key),
    deps.defaultTransport
  )
  const workspaceManager = new WorkspaceConfigManager(configStorage, httpWorkspaceFetcher)
  workspaceManager.initialize()

  const userManager = new LocalUserManager(
    deps.hackleDeviceId,
    deps.userStorage,
    deps.userTargetFetcher,
    deps.userCohortFetcher,
    deps.campaignParser,
    deps.pageManager,
    deps.clock,
    deps.previousUser,
    deps.initUser
  )

  return {
    userManager,
    workspaceConfigFetcher: workspaceManager,
    workspaceEvaluationFetcher: new NoopWorkspaceEvaluationFetcher(),
    workspaceEvaluationManager: null,
    workspaceManager,
    synchronizers: [workspaceManager, userManager]
  }
}

export function buildRemoteEvaluationComponents(deps: EvaluationModeDeps): EvaluationModeComponents {
  const client = new HttpRemoteEvaluateClient(
    Urls.workspaceEvaluate(deps.config.sdkUrl),
    Urls.entityEvaluate(deps.config.sdkUrl),
    deps.defaultTransport
  )
  const scopedStorage = KeyTransformStorage.postfix(deps.localStorage, `_${deps.sdkKey.slice(0, 8)}`)
  // local-mode config cache is never read in remote mode; drop it so a stale blob can't linger
  BrowserWorkspaceConfigStorage.clear(scopedStorage)
  const evaluationStorage = new BrowserWorkspaceEvaluationStorage(deps.sdk, scopedStorage)
  const evaluationManager = new WorkspaceEvaluationManager(
    new FullWorkspaceRemoteEvaluator(client),
    new PartialWorkspaceRemoteEvaluator(client),
    evaluationStorage,
    new LruWorkspaceEvaluationCache(4)
  )
  evaluationManager.initialize()

  const userManager = new RemoteUserManager(
    deps.hackleDeviceId,
    deps.userStorage,
    deps.pageManager,
    deps.clock,
    deps.previousUser,
    deps.initUser,
    evaluationManager
  )

  return {
    userManager,
    workspaceConfigFetcher: new NoopWorkspaceConfigFetcher(),
    workspaceEvaluationFetcher: evaluationManager,
    workspaceEvaluationManager: evaluationManager,
    workspaceManager: evaluationManager,
    synchronizers: [userManager]
  }
}
